import { useEffect, useState, type ChangeEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BrandSettings, BrandUpdate, SessionBootstrap } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useSession } from '../../lib/auth/session.js';
import { Field, Toggle } from '../../components/ui/Form.js';
import { ErrorBanner, Segmented, CardHead } from '../../components/ui/Primitives.js';
import { RowAction } from '../../components/ui/RowAction.js';

/**
 * A brand (migration 0087): ArcNet's own, or a reseller's for its clients' portals and email.
 * Resellers brand; clients do not (owner, 18 September 2026). The preview beside the form is the
 * real email, rendered by the code that sends it, with the changes not yet saved.
 */

const base = (resellerId: string | null) => (resellerId ? `/v1/resellers/${resellerId}/brand` : '/v1/brand');
type Draft = {
  enabled: boolean; name: string; accent: string; gradient: boolean; from: string; to: string;
  support_email: string; support_phone: string; website: string; footer: string;
};
const HEX = /^#[0-9a-fA-F]{6}$/;

const draftOf = (b: BrandSettings): Draft => ({
  enabled: b.enabled, name: b.name, accent: b.accent,
  gradient: !!(b.gradient_from && b.gradient_to), from: b.gradient_from ?? b.accent, to: b.gradient_to ?? b.accent,
  support_email: b.support_email ?? '', support_phone: b.support_phone ?? '',
  website: b.website ?? '', footer: b.footer ?? '',
});
const bodyOf = (d: Draft): BrandUpdate => ({
  enabled: d.enabled, name: d.name.trim(), accent: d.accent,
  gradient_from: d.gradient ? d.from : null, gradient_to: d.gradient ? d.to : null,
  support_email: d.support_email.trim() || null, support_phone: d.support_phone.trim() || null,
  website: d.website.trim() || null, footer: d.footer.trim() || null,
});

export function BrandEditor({ resellerId }: { resellerId: string | null }) {
  const qc = useQueryClient();
  const setSession = useSession((s) => s.setSession);
  const key = ['brand', resellerId ?? 'platform'] as const;
  const brand = useQuery({ queryKey: key, queryFn: () => api.get<BrandSettings>(base(resellerId)) });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [kind, setKind] = useState<'invitation' | 'voicemail'>('invitation');
  const [preview, setPreview] = useState<string>('');

  useEffect(() => { if (brand.data && !draft) setDraft(draftOf(brand.data)); }, [brand.data, draft]);

  const refresh = async (b: BrandSettings) => {
    qc.setQueryData(key, b);
    setDraft(draftOf(b));
    // The portal's own look follows at once for the person who changed it.
    setSession(await api.get<SessionBootstrap>('/v1/auth/me'));
  };
  const save = useMutation({
    mutationFn: () => api.put<BrandSettings>(base(resellerId), bodyOf(draft!)),
    onSuccess: (b) => void refresh(b),
  });
  const logo = useMutation({
    mutationFn: (data: string | null) => data
      ? api.put<BrandSettings>(`${base(resellerId)}/logo`, { data })
      : api.delete<BrandSettings>(`${base(resellerId)}/logo`),
    onSuccess: (b) => void refresh(b),
  });

  // The preview follows the form, a moment after typing stops.
  const valid = !!draft && draft.name.trim() !== '' && HEX.test(draft.accent)
    && (!draft.gradient || (HEX.test(draft.from) && HEX.test(draft.to)));
  useEffect(() => {
    if (!draft || !valid) return;
    const t = setTimeout(() => {
      api.post<{ html: string }>(`${base(resellerId)}/preview`, { brand: bodyOf(draft), kind })
        .then((r) => setPreview(r.html)).catch(() => undefined);
    }, 400);
    return () => clearTimeout(t);
  }, [draft, kind, valid, resellerId, brand.data?.logo_url]);

  const pickLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result ?? '');
      logo.mutate(s.slice(s.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  };

  if (brand.error) return <ErrorBanner error={brand.error} />;
  if (!draft || !brand.data) return <div className="arc-card arc-card-body"><div className="arc-skeleton" style={{ height: 160 }} /></div>;
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft({ ...draft, [k]: v });
  const dirty = JSON.stringify(bodyOf(draft)) !== JSON.stringify(bodyOf(draftOf(brand.data)));

  return (
    <div className="arc-card" style={{ marginBottom: 14 }}>
      <CardHead title="Brand" sub={resellerId ? 'this reseller’s portal and its clients’ portals and email' : 'ArcNet’s own, and anyone without a reseller brand'} />
      <div className="arc-card-body">
        {(save.error || logo.error) && <ErrorBanner error={save.error ?? logo.error} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          <div className="arc-stack" style={{ gap: 0 }}>
            {resellerId && (
              <div style={{ marginBottom: 14 }}>
                <Toggle id="brand-on" label="Use this brand" checked={draft.enabled}
                  hint="Off: this reseller and its clients see ArcNet’s brand."
                  onChange={(v) => set('enabled', v)} />
              </div>
            )}
            <Field label="Name" htmlFor="brand-name">
              <input id="brand-name" className="arc-input" maxLength={60} value={draft.name}
                onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label="Colour" htmlFor="brand-accent"
              hint="Buttons and links in the portal and in email. Dark enough for white text on it.">
              <ColourInput id="brand-accent" value={draft.accent} onChange={(v) => set('accent', v)} />
            </Field>
            <div style={{ marginBottom: 12 }}>
              <Toggle id="brand-gradient" label="Header gradient" checked={draft.gradient}
                hint="The email header, sign-in panel and logo tile, dark to light. Off: the colour above."
                onChange={(v) => set('gradient', v)} />
              {draft.gradient && (
                <div className="arc-row" style={{ gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
                  <ColourInput id="brand-from" label="From" value={draft.from} onChange={(v) => set('from', v)} />
                  <ColourInput id="brand-to" label="To" value={draft.to} onChange={(v) => set('to', v)} />
                </div>
              )}
            </div>
            <Field label="Logo" hint="A mark shown beside the name, white or light on a transparent background: PNG or JPEG, up to 256 KB. Without one, the first letter stands in.">
              <div className="arc-row" style={{ gap: 10, flexWrap: 'wrap' }}>
                {brand.data.logo_url && (
                  <img src={brand.data.logo_url} alt="" style={{ height: 28, maxWidth: 160, background: draft.gradient ? draft.from : draft.accent, padding: 6, borderRadius: 'var(--arc-r-sm)' }} />
                )}
                <label className="arc-btn arc-btn-sm" style={{ cursor: 'pointer' }}>
                  {logo.isPending ? 'Uploading…' : brand.data.logo_url ? 'Replace' : 'Upload'}
                  <input type="file" accept="image/png,image/jpeg" hidden onChange={pickLogo} />
                </label>
                {brand.data.logo_url && (
                  <RowAction kind="delete" label="Remove logo" onClick={() => logo.mutate(null)} />
                )}
              </div>
            </Field>
            <Field label="Support email" htmlFor="brand-email" hint="Replies go here, and it is shown at the foot of every email.">
              <input id="brand-email" type="email" className="arc-input" value={draft.support_email}
                placeholder="support@example.co.za" onChange={(e) => set('support_email', e.target.value)} />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Support phone" htmlFor="brand-phone">
                <input id="brand-phone" className="arc-input" maxLength={40} value={draft.support_phone}
                  onChange={(e) => set('support_phone', e.target.value)} />
              </Field>
              <Field label="Website" htmlFor="brand-web">
                <input id="brand-web" className="arc-input" maxLength={200} value={draft.website}
                  placeholder="example.co.za" onChange={(e) => set('website', e.target.value)} />
              </Field>
            </div>
            <Field label="Footer line" htmlFor="brand-footer" hint="Optional, under every email.">
              <input id="brand-footer" className="arc-input" maxLength={300} value={draft.footer}
                onChange={(e) => set('footer', e.target.value)} />
            </Field>
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
                disabled={!dirty || !valid || save.isPending} onClick={() => save.mutate()}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
              {dirty && (
                <button type="button" className="arc-btn arc-btn-sm" onClick={() => setDraft(draftOf(brand.data!))}>Undo</button>
              )}
            </div>
          </div>

          <div className="arc-stack" style={{ gap: 8 }}>
            <Segmented label="Preview" value={kind} onChange={setKind}
              options={[['invitation', 'Invitation'], ['voicemail', 'Voicemail']] as const} />
            <iframe title="Email preview" sandbox="" srcDoc={preview}
              style={{ width: '100%', height: 520, border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r-sm)', background: '#f4f5f7' }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** A colour: the picker and the #rrggbb it holds, kept in step. */
function ColourInput({ id, value, onChange, label }: {
  id: string; value: string; onChange: (v: string) => void; label?: string;
}) {
  return (
    <div className="arc-row" style={{ gap: 8 }}>
      {label && <label className="arc-subtle" htmlFor={id} style={{ fontSize: 12 }}>{label}</label>}
      <input type="color" aria-label={`Pick ${label ?? 'a colour'}`} value={HEX.test(value) ? value.toLowerCase() : '#168550'}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 38, height: 34, padding: 2, border: '1px solid var(--arc-border-strong)', borderRadius: 'var(--arc-r-sm)', background: 'var(--arc-surface)' }} />
      <input id={id} className="arc-input arc-mono" style={{ maxWidth: 110 }} maxLength={7}
        value={value} aria-invalid={!HEX.test(value)} onChange={(e) => onChange(e.target.value.trim())} />
    </div>
  );
}
